import { ReactNode, useMemo } from 'react'
import { IOnSort } from '../../../hooks/usePagable'
import randomIntFromInterval from '../../../utils/randomIntFromInterval'
import TableSkeloton from '../skeletons/TableSkeloton'
import TableThead from './TableThead'
import NoData from '../empty/NoData'
import { IPageParams } from '../../../services/IApi'
import { IPagable } from '../../../services/IPagable'
import ProgressLine from '../progress/ProgressLine/ProgressLine'

export type TUpdatePageParams = (newParams: Partial<IPageParams>) => void
export type ITableSize = 'small' | 'medium'

export interface ITableTh {
  title?: ReactNode
  sortCol?: string
  align?: 'left' | 'center' | 'right'
  cellsClassName?: string
}
export interface ITableThead {
  thead: ITableTh[]
  isPending?: boolean
  className?: string
  pagable?: IPagable
  onSort?: IOnSort
  size?: ITableSize
}
export interface ITable {
  title?: ReactNode
  size?: ITableSize
  isPending?: boolean
  thead?: ITableTh[]
  tbody?: {
    onClick?: () => unknown
    className?: string
    data?: React.ReactNode[]
  }[]
  trs?: ReactNode[]
  options?: Array<React.ReactNode | string>
  tbodyClassName?: string
  theadClassName?: string
  pagable?: IPagable
  onSort?: IOnSort
}

export default function Table({
  title,
  size = 'small',
  isPending = false,
  thead,
  tbody,
  trs,
  options,
  pagable,
  onSort,
  tbodyClassName = '',
  theadClassName = '',
}: ITable) {
  const saltForKeys = useMemo(() => Math.random(), [])

  return (
    <div>
      {(!!title || options?.length) && (
        <div className='flex gap-1'>
          {[...(title ? [<div className='flex'>{title}</div>] : []), ...(options || [])].map(
            (item, indx) => (
              <div key={`${saltForKeys}-${indx}`}>{item}</div>
            ),
          )}
        </div>
      )}

      <div className='mt-4 flow-root px-4 sm:px-6 lg:px-8 '>
        <div className='-mx-4 -my-2 sm:-mx-6 lg:-mx-8'>
          <div className='inline-block min-w-full py-2 align-middle'>
            {!tbody?.length && !trs?.length ? (
              isPending ? (
                <TableSkeloton rows={randomIntFromInterval(3, 8)} cols={thead?.length} />
              ) : (
                <NoData />
              )
            ) : (
              <table className='min-w-full border-separate border-spacing-0 border-border_bg border-[1px]'>
                {!!thead?.length && (
                  <TableThead
                    isPending={isPending}
                    thead={thead}
                    pagable={pagable}
                    onSort={onSort}
                    className={theadClassName}
                    size={size}
                  />
                )}

                <tbody className={tbodyClassName}>
                  <tr className='border-[0px] border-none'>
                    <td colSpan={thead?.length} height={4}>
                      {isPending && <ProgressLine heightPx={2} />}
                    </td>
                  </tr>

                  {trs?.length
                    ? (trs || []).map((tr) => {
                        return tr
                      })
                    : (tbody || []).map(
                        ({ onClick, data, className }, rowIndx) =>
                          !!data?.length && (
                            <tr
                              key={`row_${rowIndx}`}
                              className={`
                            ${onClick ? 'hover:bg-gray-100' : 'hover:bg-gray-50'}
                            ${className || ''}
                          `}
                              {...(onClick
                                ? {
                                    tabIndex: -1,
                                    role: 'button',
                                    onClick,
                                  }
                                : {})}
                            >
                              {data.map((cell, cellIndx) => (
                                <td
                                  key={`row_${rowIndx}_${cellIndx}`}
                                  className={`
                                whitespace-pre-line border-t-[1px] border-gray-200 font-normal text-black
                                ${
                                  {
                                    small: 'py-2 px-2 text-sm',
                                    medium: 'py-4 pl-4 pr-3 sm:pl-6 lg:pl-8',
                                  }[size]
                                }
                                first:pl-4 last:pr-4
                              `}
                                >
                                  <div
                                    className={`flex
                                ${
                                  {
                                    left: 'justify-left',
                                    center: 'justify-center',
                                    right: 'justify-end',
                                  }[(thead && thead?.[cellIndx]?.align) || 'left']
                                }
                                ${thead?.[cellIndx]?.cellsClassName || ''} 
                              `}
                                  >
                                    {cell}
                                  </div>
                                </td>
                              ))}
                            </tr>
                          ),
                      )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
